/**
 * Fetches a pinned Simple Icons SVG for admin import. End-user devices never hit Simple
 * Icons — only admins import through this path, and the upload pipeline rasterizes the
 * result at the size the app draws it.
 */

const MAX_SVG_BYTES = 64 * 1024;

/**
 * Validated, brand-filled Simple Icons markup, ready for `uploadCatalogImage`.
 *
 * Leave `brandHex` unset for merchant icons: the upload pipeline knocks white out to
 * transparent and forces every other opaque pixel to white before rasterizing.
 */
export async function fetchSimpleIconFile(
  svgUrl: string,
  brandHex?: string | null,
): Promise<File> {
  const svgText = await fetchValidatedSimpleIconText(svgUrl);
  const colored = applyBrandFill(svgText, brandHex);
  const slug = svgUrl.split("/").pop()?.replace(/\.svg$/i, "") || "icon";
  return new File([colored], `${slug}.svg`, { type: "image/svg+xml" });
}

async function fetchValidatedSimpleIconText(svgUrl: string): Promise<string> {
  const res = await fetch(svgUrl);
  if (!res.ok) {
    throw new Error(`Simple Icons fetch failed (${res.status}). Check the slug / CDN pin.`);
  }

  const svgText = await res.text();
  if (svgText.length > MAX_SVG_BYTES) {
    throw new Error("SVG is unexpectedly large — refusing to import.");
  }
  if (!/<svg[\s>]/i.test(svgText) || !svgText.includes("viewBox")) {
    throw new Error("Response does not look like a Simple Icons SVG.");
  }
  // Reject anything that tries to pull remote resources.
  if (/<script|xlink:href\s*=\s*["']https?:|href\s*=\s*["']https?:/i.test(svgText)) {
    throw new Error("SVG contains disallowed remote references.");
  }
  return svgText;
}

function applyBrandFill(svg: string, brandHex?: string | null): string {
  const hex = (brandHex ?? "000000").replace(/^#/, "");
  if (!/^[0-9a-fA-F]{6}$/.test(hex)) return svg;
  // Simple Icons paths are typically fill="currentColor" or inherit.
  if (/fill\s*=\s*["']currentColor["']/i.test(svg)) {
    return svg.replace(/fill\s*=\s*["']currentColor["']/gi, `fill="#${hex}"`);
  }
  if (/<svg[^>]*>/i.test(svg) && !/<svg[^>]*\sfill=/i.test(svg)) {
    return svg.replace(/<svg\b/i, `<svg fill="#${hex}"`);
  }
  return svg;
}
